import { Controller } from '@hotwired/stimulus';

import {
  MESSAGES,
  freedLotNumbers,
  lotNumberKey,
  takenNumberState,
} from '../utils/custom_lot_rules';

/**
 * Multi Select Controller
 *
 * Multi-select de lots (métiers) avec recherche, pastilles des lots cochés et
 * « Tout sélectionner/désélectionner » par catégorie (`Form::TradesMultiSelectComponent`).
 *
 * Targets:
 * - search: champ de recherche
 * - option: chaque option (lue dynamiquement : les options ajoutées par `custom-lot` comptent)
 * - category: chaque section de catégorie
 * - selectedList: pastilles des lots cochés
 * - placeholder: message « Aucun lot sélectionné »
 *
 * Values:
 * - allowCustom: lots personnalisés du marché (défaut false). Quand vrai, une option non
 *   cochée dont le numéro est déjà pris par une option cochée est grisée (`disabled`,
 *   badge « n° NN déjà pris » référencé par `aria-describedby`), et la libération d'un
 *   numéro est annoncée dans la zone polite de `custom-lot`. Faux (fiche entreprise) :
 *   comportement strictement inchangé.
 *
 * Actions:
 * - search, updateSelectedList, removeSelection, toggleCategory, clearSearch
 *
 * Events écoutés:
 * - custom-lot:added (sur la racine, avec allowCustom) : rafraîchit pastilles et grisage
 *
 * @example
 * <div data-controller="multi-select" data-multi-select-allow-custom-value="true">
 *   <div data-multi-select-target="selectedList"></div>
 *   <div data-multi-select-target="category">
 *     <div data-multi-select-target="option">
 *       <input type="checkbox" value="01 - Démolition" id="trade_01"
 *              data-action="change->multi-select#updateSelectedList"
 *              aria-describedby="">
 *       <label for="trade_01"><span data-multi-select-part="text">01 - Démolition</span></label>
 *       <span id="trade_01_taken" data-multi-select-part="taken" hidden>n° 01 déjà pris</span>
 *     </div>
 *   </div>
 * </div>
 */
export default class extends Controller {
  static targets = [
    'search', // Champ de recherche
    'option', // Chaque checkbox de métier
    'category', // Chaque section de catégorie
    'selectedList', // Liste des métiers sélectionnés (badges)
    'placeholder', // Message "Aucun métier sélectionné"
  ];

  static values = {
    allowCustom: { type: Boolean, default: false },
  };

  connect() {
    this.previousTakenState = null;
    if (this.allowCustomValue) {
      this.customLotAddedHandler = () => this.updateSelectedList();
      this.element.addEventListener('custom-lot:added', this.customLotAddedHandler);
    }
    // Initialise l'affichage de la liste des sélections au chargement
    this.updateSelectedList();
  }

  disconnect() {
    if (this.customLotAddedHandler) {
      this.element.removeEventListener('custom-lot:added', this.customLotAddedHandler);
      this.customLotAddedHandler = null;
    }
  }

  // Filtre les options en fonction de la recherche
  search(event) {
    const query = event.target.value.toLowerCase().trim();

    this.optionTargets.forEach((option) => {
      const label = option.querySelector('label').textContent.toLowerCase();
      const match = label.includes(query);

      // Affiche/cache l'option selon si elle correspond à la recherche
      option.style.display = match ? '' : 'none';
    });

    // Affiche/cache les catégories qui n'ont plus d'options visibles
    this.categoryTargets.forEach((category) => {
      const visibleOptions = category.querySelectorAll(
        '[data-multi-select-target="option"]:not([style*="display: none"])'
      );
      category.style.display = visibleOptions.length > 0 ? '' : 'none';
    });
  }

  // Met à jour la liste des métiers sélectionnés (badges)
  updateSelectedList() {
    const selectedOptions = this.optionTargets.filter((option) => {
      return option.querySelector('input[type="checkbox"]').checked;
    });

    // Affiche/cache le placeholder
    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.style.display = selectedOptions.length === 0 ? '' : 'none';
    }

    // Met à jour la liste des badges (safe DOM construction — no innerHTML)
    if (this.hasSelectedListTarget) {
      this.selectedListTarget.replaceChildren();
      selectedOptions.forEach((option) => {
        const checkbox = option.querySelector('input[type="checkbox"]');
        const label = option.querySelector('label').textContent.trim();
        const value = checkbox.value;

        const span = document.createElement('span');
        span.className =
          'inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-100 text-primary-700 rounded-lg text-sm font-medium';
        span.textContent = label;

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'hover:bg-primary-200 rounded p-0.5 transition-colors cursor-pointer';
        btn.dataset.action = 'click->multi-select#removeSelection';
        btn.dataset.value = value;

        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('class', 'w-4 h-4');
        svg.setAttribute('fill', 'none');
        svg.setAttribute('stroke', 'currentColor');
        svg.setAttribute('viewBox', '0 0 24 24');
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('stroke-linecap', 'round');
        path.setAttribute('stroke-linejoin', 'round');
        path.setAttribute('stroke-width', '2');
        path.setAttribute('d', 'M6 18L18 6M6 6l12 12');
        svg.appendChild(path);
        btn.appendChild(svg);

        span.appendChild(document.createTextNode(' '));
        span.appendChild(btn);
        this.selectedListTarget.appendChild(span);
      });
    }

    if (this.allowCustomValue) this.#applyTakenNumbers();
  }

  // Retire un métier sélectionné en cliquant sur le badge
  removeSelection(event) {
    event.preventDefault();
    const value = event.currentTarget.dataset.value;

    // Trouve et décoche la checkbox correspondante (valeur échappée : un intitulé peut contenir ' ou \)
    const checkbox = this.element.querySelector(
      `input[type="checkbox"][value="${CSS.escape(value)}"]`
    );
    if (checkbox) {
      checkbox.checked = false;
      this.updateSelectedList();
    }
  }

  // Sélectionne/désélectionne tous les métiers d'une catégorie (les cases grisées sont ignorées)
  toggleCategory(event) {
    const category = event.currentTarget.closest('[data-multi-select-target="category"]');
    const checkboxes = Array.from(category.querySelectorAll('input[type="checkbox"]')).filter(
      (cb) => !cb.disabled
    );
    const allChecked = checkboxes.every((cb) => cb.checked);

    // Inverse l'état : si tous sont cochés, tout décocher, sinon tout cocher
    if (allChecked || !this.allowCustomValue) {
      checkboxes.forEach((checkbox) => {
        checkbox.checked = !allChecked;
      });
    } else {
      this.#checkAllWithFreeNumbers(checkboxes);
    }

    this.updateSelectedList();
  }

  // Efface la recherche
  clearSearch() {
    if (this.hasSearchTarget) {
      this.searchTarget.value = '';
      // Réaffiche toutes les options
      this.optionTargets.forEach((option) => (option.style.display = ''));
      this.categoryTargets.forEach((category) => (category.style.display = ''));
    }
  }

  // Tout cocher sans créer de doublon de numéro : une case dont le numéro est déjà pris
  // (par une case cochée ailleurs ou plus haut dans la même catégorie) reste décochée.
  #checkAllWithFreeNumbers(checkboxes) {
    const takenNumbers = new Set(
      Array.from(this.element.querySelectorAll('input[type="checkbox"]:checked'))
        .map((cb) => lotNumberKey(cb.value))
        .filter((key) => key !== null)
    );

    checkboxes.forEach((checkbox) => {
      if (checkbox.checked) return;
      const key = lotNumberKey(checkbox.value);
      if (key !== null && takenNumbers.has(key)) return;
      checkbox.checked = true;
      if (key !== null) takenNumbers.add(key);
    });
  }

  // Grise les options non cochées dont le numéro est déjà pris (allowCustom uniquement).
  #applyTakenNumbers() {
    const entries = this.optionTargets
      .map((option) => ({ option, checkbox: option.querySelector('input[type="checkbox"]') }))
      .filter(({ checkbox }) => checkbox);

    const state = takenNumberState(
      entries.map(({ checkbox }) => ({ value: checkbox.value, checked: checkbox.checked }))
    );
    entries.forEach(({ option, checkbox }, index) => {
      this.#applyTakenState(option, checkbox, state[index].disabled);
    });

    const freed = freedLotNumbers(this.previousTakenState, state);
    this.previousTakenState = state;
    if (freed.length > 0) this.#announce(freed.map(MESSAGES.numberAvailable).join('. '));
  }

  #applyTakenState(option, checkbox, disabled) {
    checkbox.disabled = disabled;

    const label = option.querySelector('label');
    const text = option.querySelector('[data-multi-select-part="text"]') ?? label;
    text?.classList.toggle('opacity-60', disabled);
    if (label) {
      label.classList.toggle('cursor-not-allowed', disabled);
      label.classList.toggle('cursor-pointer', !disabled);
    }

    const taken = option.querySelector('[data-multi-select-part="taken"]');
    if (!taken) return;
    taken.hidden = !disabled;
    if (taken.id) this.#toggleDescribedBy(checkbox, taken.id, disabled);
  }

  // Ajoute / retire un id dans aria-describedby sans écraser les autres (ex. « _offcat »).
  #toggleDescribedBy(element, id, present) {
    const ids = (element.getAttribute('aria-describedby') ?? '').split(/\s+/).filter(Boolean);
    const next = present ? [...new Set([...ids, id])] : ids.filter((token) => token !== id);

    if (next.length > 0) {
      element.setAttribute('aria-describedby', next.join(' '));
    } else {
      element.removeAttribute('aria-describedby');
    }
  }

  // Zone polite portée par `custom-lot` sur la même racine.
  #announce(message) {
    const status = this.element.querySelector('[data-custom-lot-target="status"]');
    if (status) status.textContent = message;
  }
}
